import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { jobsAPI } from '@/utils/api';
import { Search, MapPin, Briefcase, X, SlidersHorizontal, Star, ArrowRight, ShieldCheck } from 'lucide-react';
import FavouriteButton from '@/components/FavouriteButton';
import { RemoteBadge } from '@/components/RemoteBadge';
import { WORK_MODES, rwLabel } from '@/constants/remoteWork';
import axios from 'axios';

// Proper nouns (no translation needed)
const ISLANDS = [
  'Santiago', 'São Vicente', 'Sal', 'Boa Vista', 'Santo Antão',
  'Fogo', 'Maio', 'São Nicolau', 'Brava',
];
// Canonical category values stored in DB (PT) — labels are localised at render time
import { JOB_CATEGORIES as CATEGORY_VALUES } from '@/constants/jobCategories';
// Canonical employment-type values stored in DB — labels localised at render time
const EMPLOYMENT_VALUES = ['', 'full-time', 'part-time', 'contract', 'internship', 'temporary'];

export default function JobSearch() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'pt').slice(0, 2);
  const dateLocale = lang === 'fr' ? 'fr-FR' : lang === 'en' ? 'en-US' : 'pt-PT';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [sectors, setSectors] = useState([]);

  // Initialise filters from URL — preserves shareable links + back navigation
  const [filters, setFilters] = useState(() => ({
    search: searchParams.get('q') || '',
    category: searchParams.get('category') || '',
    business_category: searchParams.get('sector') || '',
    location: searchParams.get('location') || '',
    employment_type: searchParams.get('type') || '',
    sort: searchParams.get('sort') || 'recent',
    verified_only: searchParams.get('verified') === '1' ? 'true' : '',
    work_mode: searchParams.get('mode') || '',
  }));

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/categories`)
      .then((r) => setSectors(r.data.items || []))
      .catch(() => setSectors([]));
  }, []);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
      );
      const { data } = await jobsAPI.getPublic(params);
      setJobs(data || []);
    } catch (e) {
      console.error('Failed to load jobs:', e);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Sync URL when filters change (shallow, no page reload)
  useEffect(() => {
    const next = new URLSearchParams();
    if (filters.search) next.set('q', filters.search);
    if (filters.category) next.set('category', filters.category);
    if (filters.business_category) next.set('sector', filters.business_category);
    if (filters.location) next.set('location', filters.location);
    if (filters.employment_type) next.set('type', filters.employment_type);
    if (filters.work_mode) next.set('mode', filters.work_mode);
    if (filters.sort && filters.sort !== 'recent') next.set('sort', filters.sort);
    setSearchParams(next, { replace: true });
  }, [filters, setSearchParams]);

  // Debounced search reload (350 ms after last keypress)
  useEffect(() => {
    const id = setTimeout(loadJobs, 350);
    return () => clearTimeout(id);
  }, [loadJobs]);

  const clearFilters = () => {
    setFilters({ search: '', category: '', business_category: '', location: '', employment_type: '', sort: 'recent', verified_only: '', work_mode: '' });
  };

  const activeFilterCount = useMemo(
    () => ['category', 'business_category', 'location', 'employment_type', 'verified_only', 'work_mode'].filter((k) => filters[k]).length,
    [filters]
  );

  // Reactive label arrays — recompute on language change
  const categoryOptions = useMemo(
    () => CATEGORY_VALUES.map((value) => ({
      value,
      label: t(`jobSearch.categories.${value}`, { defaultValue: value }),
    })),
    [t, lang] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const employmentOptions = useMemo(
    () => EMPLOYMENT_VALUES.map((value) => ({
      value,
      label: t(`jobSearch.employmentTypes.${value}`),
    })),
    [t, lang] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const getTitle = (job) => {
    if (lang === 'en') return job.title_en || job.title_pt;
    if (lang === 'fr') return job.title_fr || job.title_pt;
    return job.title_pt;
  };
  const getDescription = (job) => {
    if (lang === 'en') return job.description_en || job.description_pt;
    if (lang === 'fr') return job.description_fr || job.description_pt;
    return job.description_pt;
  };
  const getCategory = (job) => {
    if (!job?.category) return '';
    // Prefer translated category coming from backend; otherwise translate the canonical PT value
    if (lang === 'en' && job.category_en) return job.category_en;
    if (lang === 'fr' && job.category_fr) return job.category_fr;
    if (lang !== 'pt') {
      const key = `jobSearch.categories.${job.category}`;
      const translated = t(key, { defaultValue: '' });
      if (translated && translated !== key) return translated;
    }
    return job.category;
  };

  const fmtSalary = (job) => {
    if (job.salary_range_min && job.salary_range_max) {
      const a = Number(job.salary_range_min).toLocaleString(dateLocale);
      const b = Number(job.salary_range_max).toLocaleString(dateLocale);
      return `${a}–${b} ${job.currency || 'CVE'}`;
    }
    return null;
  };

  return (
    <>
      <Helmet>
        <title>{t('jobSearch.metaTitle')}</title>
        <meta name="description" content={t('jobSearch.metaDescription')} />
        <link rel="canonical" href="https://trabadju.cv/jobs/search" />
        <meta property="og:title" content={t('jobSearch.metaTitle')} />
        <meta property="og:description" content={t('jobSearch.ogDescription')} />
        <meta property="og:url" content="https://trabadju.cv/jobs/search" />
      </Helmet>

      <main className="bg-slate-50 min-h-screen">
        {/* ─────────── Page header ─────────── */}
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8">
            <p className="text-xs font-semibold tracking-[0.10em] uppercase text-blue-700 mb-3">
              {t('jobSearch.eyebrow')}
            </p>
            <h1
              className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tighter text-slate-900 leading-[1.1] max-w-3xl"
              data-testid="jobsearch-h1"
            >
              {t('jobSearch.h1')}
            </h1>
            <p className="mt-3 text-[15px] text-slate-600 max-w-2xl leading-[1.6]">
              {t('jobSearch.subtitle')}
            </p>
          </div>
        </header>

        {/* ─────────── Search + filters ─────────── */}
        <section aria-labelledby="filters-title" className="bg-white border-b border-slate-200 sticky top-16 z-30">
          <h2 id="filters-title" className="sr-only">{t('jobSearch.filtersAria')}</h2>
          <form
            onSubmit={(e) => { e.preventDefault(); loadJobs(); }}
            className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5"
          >
            {/* Primary row: search + island + CTA */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-stretch">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" aria-hidden="true" />
                <label htmlFor="jobsearch-input" className="sr-only">{t('jobSearch.searchLabel')}</label>
                <input
                  id="jobsearch-input"
                  type="search"
                  placeholder={t('jobSearch.searchPlaceholder')}
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full h-11 pl-10 pr-3 text-[15px] border border-slate-300 rounded-lg bg-white focus:outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-200 transition placeholder:text-slate-400"
                  data-testid="search-input"
                />
              </div>
              <label htmlFor="jobsearch-location" className="sr-only">{t('jobSearch.islandLabel')}</label>
              <select
                id="jobsearch-location"
                value={filters.location}
                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                className="h-11 px-3 text-[15px] border border-slate-300 rounded-lg bg-white focus:outline-none focus:border-slate-900 sm:w-44"
                data-testid="filter-location-select"
              >
                <option value="">{t('jobSearch.allIslands')}</option>
                {ISLANDS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
              <label htmlFor="jobsearch-workmode" className="sr-only">{rwLabel(lang, 'workMode')}</label>
              <select
                id="jobsearch-workmode"
                value={filters.work_mode}
                onChange={(e) => setFilters({ ...filters, work_mode: e.target.value })}
                className="h-11 px-3 text-[15px] border border-slate-300 rounded-lg bg-white focus:outline-none focus:border-slate-900 sm:w-44"
                data-testid="filter-workmode-select"
              >
                <option value="">{rwLabel(lang, 'allModes')}</option>
                {WORK_MODES.map((m) => <option key={m} value={m}>{rwLabel(lang, m)}</option>)}
              </select>
              <button
                type="button"
                onClick={() => setFilters({ ...filters, verified_only: filters.verified_only ? '' : 'true' })}
                className={`h-11 px-4 inline-flex items-center justify-center gap-2 text-[15px] font-semibold rounded-lg border transition ${filters.verified_only ? 'bg-blue-600 border-blue-600 text-white' : 'text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                aria-pressed={Boolean(filters.verified_only)}
                data-testid="toggle-verified-btn"
              >
                <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                {t('jobSearch.verifiedOnly')}
              </button>
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="h-11 px-4 inline-flex items-center justify-center gap-2 text-[15px] font-semibold text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 transition"
                data-testid="toggle-advanced-btn"
                aria-expanded={showAdvanced}
                aria-controls="jobsearch-advanced"
              >
                <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
                {t('jobSearch.filtersBtn')}
                {activeFilterCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-blue-600 text-white text-[11px] font-bold rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>

            {/* Sector pills — filter by employer business_category */}
            {sectors.length > 0 && (
              <div className="mt-3 -mx-2 px-2 overflow-x-auto" data-testid="sector-pills">
                <div className="flex items-center gap-2 whitespace-nowrap pb-1">
                  <button
                    type="button"
                    onClick={() => setFilters({ ...filters, business_category: '' })}
                    className={`px-3 py-1.5 rounded-full text-[13px] font-semibold border transition-all ${
                      !filters.business_category
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                    }`}
                    data-testid="sector-pill-all"
                  >
                    {t('jobSearch.allSectors', { defaultValue: 'Todos os sectores' })}
                  </button>
                  {sectors.map((s) => {
                    const label = lang === 'en' ? (s.name_en || s.name_pt)
                                : lang === 'fr' ? (s.name_fr || s.name_pt)
                                : s.name_pt;
                    const active = filters.business_category === s.slug;
                    return (
                      <button
                        key={s.slug}
                        type="button"
                        onClick={() => setFilters({ ...filters, business_category: active ? '' : s.slug })}
                        className={`px-3 py-1.5 rounded-full text-[13px] font-semibold border transition-all ${
                          active
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-slate-700 border-slate-200 hover:border-blue-400 hover:text-blue-700'
                        }`}
                        data-testid={`sector-pill-${s.slug}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Advanced filters */}
            {showAdvanced && (
              <div id="jobsearch-advanced" className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="advanced-filters">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wider" htmlFor="f-category">{t('jobSearch.categoryLabel')}</label>
                  <select
                    id="f-category"
                    value={filters.category}
                    onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                    className="w-full h-10 px-3 text-sm border border-slate-300 rounded-lg bg-white"
                    data-testid="filter-category-select"
                  >
                    <option value="">{t('jobSearch.allCategories')}</option>
                    {categoryOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wider" htmlFor="f-type">{t('jobSearch.employmentLabel')}</label>
                  <select
                    id="f-type"
                    value={filters.employment_type}
                    onChange={(e) => setFilters({ ...filters, employment_type: e.target.value })}
                    className="w-full h-10 px-3 text-sm border border-slate-300 rounded-lg bg-white"
                    data-testid="filter-employment-select"
                  >
                    {employmentOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wider" htmlFor="f-sort">{t('jobSearch.sortLabel')}</label>
                  <select
                    id="f-sort"
                    value={filters.sort}
                    onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
                    className="w-full h-10 px-3 text-sm border border-slate-300 rounded-lg bg-white"
                    data-testid="filter-sort-select"
                  >
                    <option value="recent">{t('jobSearch.sortRecent')}</option>
                    <option value="salary_high">{t('jobSearch.sortSalaryHigh')}</option>
                    <option value="salary_low">{t('jobSearch.sortSalaryLow')}</option>
                  </select>
                </div>
              </div>
            )}
          </form>
        </section>

        {/* ─────────── Results summary ─────────── */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 flex items-center justify-between gap-3">
          <p className="text-[13px] sm:text-sm text-slate-600" data-testid="results-count" aria-live="polite">
            {loading ? (
              t('jobSearch.searching')
            ) : (
              <>
                <strong className="text-slate-900 font-semibold">{jobs.length}</strong>{' '}
                {t(jobs.length === 1 ? 'jobSearch.found_one' : 'jobSearch.found_other')}
                {activeFilterCount > 0 && (
                  <span className="text-slate-500"> · {t('jobSearch.activeFilters', { count: activeFilterCount })}</span>
                )}
              </>
            )}
          </p>
          {(activeFilterCount > 0 || filters.search) && !loading && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1 text-[13px] font-semibold text-slate-700 hover:text-slate-900"
              data-testid="clear-filters-btn"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
              {t('jobSearch.clearFilters')}
            </button>
          )}
        </div>

        {/* ─────────── Jobs list ─────────── */}
        <section
          aria-labelledby="results-title"
          className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-16"
        >
          <h2 id="results-title" className="sr-only">{t('jobSearch.resultsAria')}</h2>

          {loading ? (
            <ul className="space-y-3" aria-busy="true">
              {[...Array(4)].map((_, i) => (
                <li key={i} className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6">
                  <div className="animate-pulse space-y-3">
                    <div className="h-5 bg-slate-200 rounded w-2/3"></div>
                    <div className="h-3 bg-slate-100 rounded w-1/3"></div>
                    <div className="h-3 bg-slate-100 rounded w-5/6"></div>
                  </div>
                </li>
              ))}
            </ul>
          ) : jobs.length === 0 ? (
            <div
              className="bg-white rounded-xl border border-slate-200 p-10 sm:p-14 text-center max-w-2xl mx-auto mt-6"
              data-testid="empty-state"
            >
              <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-5">
                <Briefcase className="h-6 w-6 text-slate-500" aria-hidden="true" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight mb-2">
                {t('jobSearch.emptyTitle')}
              </h2>
              <p className="text-[15px] text-slate-600 leading-relaxed max-w-md mx-auto mb-6">
                {t('jobSearch.emptyBody')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  type="button"
                  onClick={clearFilters}
                  className="inline-flex items-center justify-center h-11 px-5 bg-slate-900 hover:bg-slate-800 text-white text-[15px] font-semibold rounded-lg transition"
                  data-testid="empty-clear-btn"
                >
                  {t('jobSearch.clearFilters')}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/candidate/register')}
                  className="inline-flex items-center justify-center gap-2 h-11 px-5 bg-white border border-slate-300 hover:border-slate-900 text-slate-900 text-[15px] font-semibold rounded-lg transition"
                  data-testid="empty-alerts-btn"
                >
                  {t('jobSearch.emptyAlertsBtn')}
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="jobs-list">
              {jobs.map((job) => {
                const salary = fmtSalary(job);
                const empLabel = job.employment_type
                  ? t(`jobSearch.employmentTypes.${job.employment_type}`, { defaultValue: job.employment_type })
                  : null;
                return (
                  <li key={job.id} className="contents">
                    <article
                      onClick={() => navigate(`/jobs/${job.id}`)}
                      className="group bg-white rounded-xl border border-slate-200 hover:border-slate-900 hover:shadow-sm transition cursor-pointer p-5 sm:p-6"
                      data-testid={`job-card-${job.id}`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <h3 className="text-[17px] sm:text-lg font-bold text-slate-900 leading-snug group-hover:text-blue-700 transition-colors line-clamp-2 flex-1">
                          {getTitle(job)}
                        </h3>
                        <div className="flex items-center gap-1.5 flex-none">
                          <RemoteBadge job={job} />
                          {job.company_verified && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold bg-blue-50 text-blue-800 rounded" data-testid={`job-verified-${job.id}`}>
                              <ShieldCheck className="h-3 w-3" aria-hidden="true" />
                              {t('jobSearch.cardVerified')}
                            </span>
                          )}
                          {job.is_featured && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold bg-amber-50 text-amber-800 rounded">
                              <Star className="h-3 w-3 fill-amber-500 text-amber-500" aria-hidden="true" />
                              {t('jobSearch.cardFeatured')}
                            </span>
                          )}
                          <span onClick={(e) => e.stopPropagation()}>
                            <FavouriteButton jobId={job.id} />
                          </span>
                        </div>
                      </div>

                      <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-slate-500 mb-3">
                        {job.location && (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                            {job.location}
                          </span>
                        )}
                        {job.category && (
                          <span className="inline-flex items-center gap-1">
                            <Briefcase className="h-3.5 w-3.5" aria-hidden="true" />
                            {getCategory(job)}
                          </span>
                        )}
                        {empLabel && (
                          <span className="px-1.5 py-0.5 text-[11px] font-medium bg-slate-100 text-slate-700 rounded">
                            {empLabel}
                          </span>
                        )}
                        {(job.vacancies_count ?? 1) > 1 && (
                          <span className="px-1.5 py-0.5 text-[11px] font-medium bg-emerald-50 text-emerald-800 rounded" data-testid={`vacancies-${job.id}`}>
                            {t('jobSearch.cardVacancies', { count: job.vacancies_count })}
                          </span>
                        )}
                      </p>

                      {getDescription(job) && (
                        <p className="text-[14px] text-slate-600 line-clamp-2 mb-3 leading-relaxed">
                          {getDescription(job)}
                        </p>
                      )}

                      <footer className="flex items-center justify-between gap-3 pt-3 border-t border-slate-100">
                        <p className="text-[14px] font-semibold text-slate-900">
                          {salary || <span className="text-slate-500 font-normal">{t('jobSearch.cardSalaryTBD')}</span>}
                        </p>
                        <span aria-hidden="true" className="text-[13px] font-semibold text-blue-700 group-hover:translate-x-1 transition-transform">
                          {t('jobSearch.cardSeeDetails')}
                        </span>
                      </footer>
                    </article>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </main>
    </>
  );
}
